import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';

import { Screen } from '../../app/screen';
import { LtrText, UserSpan, UserText } from '../../components/UserText';

/**
 * صفحة تعذيب bidi (§3.2 القاعدة 7): أسوأ الحالات مجتمعة في مكان واحد كي تُلتقط
 * لها صورة في CI في الوضعين. dev/CI فقط — لا رابط إليها من التنقل.
 *
 * كل حالة هنا تعرض السلوك **الصحيح**: أي انقلاب مرئي في هذه القائمة يعني أن
 * نصًا خرج من `UserText`/`UserSpan`/`LtrText` في مكان ما.
 */
/**
 * بند R-20: هذه الصفحة **سقالة تطوير** — ملعب ثيم/بالتة وتعذيب bidi. وكانت تُشحن في
 * الإنتاج: مسارٌ بالغ في شجرة المالك وchunk كامل في `dist/`.
 *
 * والحل إبطالها بالبناء لا حذفها: `import.meta.env.DEV` ثابتٌ يُستبدَل وقت البناء،
 * فتصير الشرطية `false` في الإنتاج ويسقط المكوّن كله من الشجرة مع كل ما يستورده
 * (tree-shaking) — بينما يبقى الملف حاضرًا في التطوير حيث نفعه.
 *
 * ولم أحذفها لأنها تُلتقط لها صورة عند كل عمل على الاتجاه والثيم؛ ولم أستعمل
 * `routeFileIgnorePattern` لأن `routeTree.gen.ts` **متعقَّب في git**، فاستثناءٌ مشروط
 * بالإنتاج يجعل كل بناء يُوسِّخ الشجرة بملفٍّ مولَّد يتقلّب مع آخر أمر شُغِّل.
 */
export const Route = createFileRoute('/dev/bidi')({
  component: import.meta.env.DEV ? BidiTortureScreen : DevOnlyNotice,
});

const INLINE_CASES: readonly { label: string; value: string }[] = [
  { label: 'عنوان يبدأ برقم', value: '3 صناديق رفوف للمعرض' },
  { label: 'عنوان يبدأ بلاتيني', value: 'Retail POS revamp — المرحلة الثانية' },
  { label: 'لاتيني داخل عربي داخل أقواس', value: 'راجع العرض (final version 2) قبل الاثنين' },
  { label: 'عربي داخل لاتيني', value: 'Send the فاتورة to the accountant' },
  { label: 'ترقيم وعلامات في النهاية', value: 'اتصل بالمورّد!!! (رقم 3)' },
  { label: 'وسم عربي مع رمز', value: '#متجر_الرفوف' },
  { label: 'خليط أرقام هاتف', value: 'جوال المورّد 0501234567 وواتساب +966 55 123 4567' },
  { label: 'اسم ملف لاتيني في جملة عربية', value: 'أرفقت الملف quote-final-v2.pdf في المجلد' },
];

const LTR_CASES: readonly { label: string; value: string }[] = [
  { label: 'URL داخل نص عربي', value: 'https://flow.internal/projects/01J1ZC8XK9WVQ4T2M8R5E7N3PA' },
  { label: 'معرّف ULID', value: '01J1ZC8XK9WVQ4T2M8R5E7N3PA' },
  { label: 'مسار ملف', value: '/data/media/2026/07/01J1ZC8XK9.m4a' },
  { label: 'مبلغ وكلفة', value: '1,240.50 SAR · $0.0143' },
  { label: 'وقت وتاريخ', value: '2026-07-29 14:30' },
];

const BLOCK_CASES: readonly { label: string; value: string }[] = [
  {
    label: 'فقرتان مختلفتا الاتجاه في قيد واحد',
    value: 'وقفنا على عرض السعر من المورّد الجديد.\nThe supplier quote is 12% cheaper than the current one.',
  },
  {
    label: 'قائمة مختلطة داخل كتلة',
    value: '- مراجعة الأسعار\n- Update the POS firmware\n- إبلاغ المحاسب',
  },
  {
    label: 'سطر عربي ينتهي برابط',
    value: 'التفاصيل كلها هنا: https://example.com/a/b?q=1',
  },
];

function BidiTortureScreen() {
  return (
    <Screen title="تعذيب bidi">
      <CaseGroup title="سطري داخل جملة واجهة (القاعدة 2)">
        {INLINE_CASES.map((item) => (
          <Row key={item.label} label={item.label}>
            أضافه Hermes إلى <UserSpan>{item.value}</UserSpan> قبل ساعتين
          </Row>
        ))}
      </CaseGroup>

      <CaseGroup title="أرقام ومعرفات ومسارات (القاعدة 5)">
        {LTR_CASES.map((item) => (
          <Row key={item.label} label={item.label}>
            القيمة <LtrText>{item.value}</LtrText> كما وصلت
          </Row>
        ))}
      </CaseGroup>

      <CaseGroup title="كتل متعددة الأسطر (القاعدة 3)">
        {BLOCK_CASES.map((item) => (
          <Row key={item.label} label={item.label}>
            <UserText block className="whitespace-pre-line text-body-lg">
              {item.value}
            </UserText>
          </Row>
        ))}
      </CaseGroup>

      <CaseGroup title="حقول الإدخال (القاعدة 4)">
        <Row label="حقل سطر واحد">
          <input
            dir="auto"
            defaultValue="Retail POS revamp"
            className="w-full rounded-sm border border-border bg-surface px-3 py-2 text-body"
          />
        </Row>
        <Row label="حقل متعدد الأسطر">
          <textarea
            dir="auto"
            rows={2}
            defaultValue={'كلم مورد الأكياس بكرة\ncall the shelving supplier too'}
            className="w-full rounded-sm border border-border bg-surface px-3 py-2 text-body"
          />
        </Row>
      </CaseGroup>
    </Screen>
  );
}

function CaseGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-heading">{title}</h2>
      <ul className="flex flex-col gap-2">{children}</ul>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <li className="rounded-lg border border-border bg-surface p-4">
      <p className="pb-1 text-micro text-text-muted">{label}</p>
      <div className="text-body">{children}</div>
    </li>
  );
}

/** بديل الإنتاج: نصٌّ ساكن بلا تبعيات — وجوده وحده يكفي ليُشذَّب الملعب كله. */
function DevOnlyNotice() {
  return <p className="p-6 text-body text-text-muted">صفحة تطوير — غير متاحة في النسخة المنشورة.</p>;
}
